import React, { useMemo } from 'react';
import {
  format,
  startOfDay,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  addDays,
  subDays,
} from 'date-fns';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';
import { StatCard } from './StatCard';

// Week starts on Sunday, matching HabitCalendar (0 = Sunday per date-fns).
const WEEK_STARTS_ON = 0;
// Trailing window length. 365 days inclusive of today.
const WINDOW_DAYS = 365;

// Pure helper: build a weekday-aligned heatmap grid for the trailing WINDOW_DAYS.
// Returns an array of weeks; each week is 7 cells running top-to-bottom Sun..Sat,
// so the total length is always a multiple of 7. Columns run oldest (left) to
// newest (right). Leading cells before the window start and trailing cells after
// today (the rest of the current week) carry inRange:false and render as empty
// gaps, keeping the grid rectangular.
export function buildHeatmapGrid(today = new Date(), { weekStartsOn = WEEK_STARTS_ON, windowDays = WINDOW_DAYS } = {}) {
  const end = startOfDay(today);
  const windowStart = subDays(end, windowDays - 1);
  const gridStart = startOfWeek(windowStart, { weekStartsOn });
  const gridEnd = endOfWeek(end, { weekStartsOn });

  const cells = eachDayOfInterval({ start: gridStart, end: gridEnd }).map((date) => ({
    date,
    inRange: date >= windowStart && date <= end,
    isToday: isSameDay(date, end),
  }));

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

// Pure helper: derive the positive aggregate stats from raw habit logs. Logs are
// deduped to one entry per calendar day so multiple logs on the same day never
// inflate counts. Only days within the trailing window contribute.
export function computeHeatmapStats(habitLogs = [], today = new Date(), { windowDays = WINDOW_DAYS } = {}) {
  const end = startOfDay(today);
  const windowStart = subDays(end, windowDays - 1);

  // Distinct logged calendar days (yyyy-MM-dd) within the window.
  const loggedKeys = new Set();
  habitLogs.forEach((log) => {
    const date = startOfDay(new Date(log.recordTs));
    if (date >= windowStart && date <= end) {
      loggedKeys.add(format(date, 'yyyy-MM-dd'));
    }
  });

  const totalLogged = loggedKeys.size;
  const last30Start = subDays(end, 29);

  // Single chronological pass over the window keeps every derived stat on local
  // Date objects — no reparsing yyyy-MM-dd strings (which would resolve as UTC
  // and shift the day/month in negative-offset timezones).
  let last30 = 0;
  let longestStreak = 0;
  let run = 0;
  const monthCounts = {};
  for (let d = windowStart; d <= end; d = addDays(d, 1)) {
    if (loggedKeys.has(format(d, 'yyyy-MM-dd'))) {
      run += 1;
      if (run > longestStreak) longestStreak = run;
      if (d >= last30Start) last30 += 1;
      const label = format(d, 'MMM yyyy');
      monthCounts[label] = (monthCounts[label] || 0) + 1;
    } else {
      run = 0;
    }
  }

  // Calendar month with the most logged days. Earliest month wins ties since the
  // loop visits months chronologically and we only replace on a strict increase.
  let bestMonth = null;
  Object.entries(monthCounts).forEach(([label, count]) => {
    if (!bestMonth || count > bestMonth.count) {
      bestMonth = { label, count };
    }
  });

  return { totalLogged, last30, longestStreak, bestMonth };
}

const dayLabel = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;

export function ContributionHeatmap({ habitLogs = [] }) {
  // Pin "today" once on mount so it stays a stable reference across renders.
  const today = useMemo(() => startOfDay(new Date()), []);

  const loggedSet = useMemo(() => {
    const set = new Set();
    habitLogs.forEach((log) => {
      set.add(format(startOfDay(new Date(log.recordTs)), 'yyyy-MM-dd'));
    });
    return set;
  }, [habitLogs]);

  const weeks = useMemo(() => buildHeatmapGrid(today), [today]);
  const stats = useMemo(() => computeHeatmapStats(habitLogs, today), [habitLogs, today]);

  // One month label per week index: the first week of each month (the leading
  // partial month is skipped so it never collides with the next label).
  const monthLabels = useMemo(() => {
    const labels = [];
    let last = -1;
    let seen = false;
    weeks.forEach((week) => {
      const month = week[0].date.getMonth();
      let label = '';
      if (week[0].inRange) {
        if (!seen) {
          seen = true;
          last = month;
        } else if (month !== last) {
          label = format(week[0].date, 'MMM');
          last = month;
        }
      }
      labels.push(label);
    });
    return labels;
  }, [weeks]);

  const renderCell = (cell) => {
    const logged = cell.inRange && loggedSet.has(format(cell.date, 'yyyy-MM-dd'));
    let bg = 'gray.100';
    if (!cell.inRange) bg = 'transparent';
    else if (logged) bg = 'green.400';

    return (
      <Box
        key={cell.date.toISOString()}
        data-testid="heatmap-cell"
        data-in-range={cell.inRange}
        data-logged={logged}
        data-today={cell.isToday}
        w="100%"
        sx={{ aspectRatio: '1' }}
        bg={bg}
        borderRadius="2px"
        boxShadow={cell.isToday ? 'inset 0 0 0 2px var(--chakra-colors-blue-400)' : undefined}
      />
    );
  };

  return (
    <Box>
      <Text fontSize="md" fontWeight="semibold" mb={3}>
        Last 365 days
      </Text>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={{ base: 0, md: 4 }}>
        <StatCard testId="heatmap-stat" label="Days logged" value={`${stats.totalLogged} / 365`} />
        <StatCard testId="heatmap-stat" label="Last 30 days" value={dayLabel(stats.last30)} />
        <StatCard testId="heatmap-stat" label="Longest streak" value={dayLabel(stats.longestStreak)} />
        <StatCard testId="heatmap-stat" label="Best month" value={stats.bestMonth ? `${stats.bestMonth.label} · ${stats.bestMonth.count}` : '—'} />
      </SimpleGrid>

      {/* The day-by-day grid needs width that small screens don't have, and a
          legible version would force an awkward second scroll axis. Below md we
          drop it and let the summary stats above carry the at-a-glance review.
          Shown from md up — the same base/md breakpoint the calendar, card width,
          and stat columns already switch on. */}
      <Box display={{ base: 'none', md: 'block' }}>
        <Flex gap={2} align="stretch">
          <Flex
            direction="column"
            justify="space-around"
            fontSize="2xs"
            color="gray.400"
            pt="14px"
            aria-hidden="true"
          >
            {['', 'M', '', 'W', '', 'F', ''].map((label, i) => (
              <Box key={i} h="11px" lineHeight="11px">
                {label}
              </Box>
            ))}
          </Flex>

          <Box flex="1" minW="0">
            <Flex mb="3px" aria-hidden="true">
              {weeks.map((week, i) => (
                <Box key={i} flex="1" minW="0" fontSize="2xs" color="gray.400" whiteSpace="nowrap" overflow="visible">
                  {monthLabels[i]}
                </Box>
              ))}
            </Flex>

            <Flex gap="3px">
              {weeks.map((week, i) => (
                <Flex key={i} direction="column" gap="3px" flex="1" minW="0">
                  {week.map(renderCell)}
                </Flex>
              ))}
            </Flex>
          </Box>
        </Flex>

        <Flex justify="flex-end" align="center" gap={2} mt={2}>
          <Text fontSize="xs" color="gray.500">
            Less
          </Text>
          <Box w="11px" h="11px" borderRadius="2px" bg="gray.100" />
          <Box w="11px" h="11px" borderRadius="2px" bg="green.400" />
          <Text fontSize="xs" color="gray.500">
            Logged
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}
